import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, formatDistanceToNow } from 'date-fns';
import type { DateRange } from '../../types';

/**
 * Normalized job shape consumed by the analytics view. Every product's
 * history row (lightning `HistoricalJob`, video/interior/ads `Job`)
 * carries these fields, so each Overview supplies a thin adapter that
 * maps its rows into this shape.
 */
export interface AnalyticsJob {
  id: string;
  created_at: string;
  status: string;
  duration_seconds: number | null;
  error: string | null;
}

interface RequestAnalyticsProps {
  /** Per-product fetcher pulling every job in the given window. */
  fetchJobs: (range: DateRange) => Promise<AnalyticsJob[]>;
  /** Optional target for the "View full statistics" link. */
  statsHref?: string;
}

type RangeKey = '24h' | '7d' | '30d';
type IntervalKey = '15m' | '1h' | '6h' | '1d';

const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24h',
  '7d': '7 Days',
  '30d': '30 Days',
};

const INTERVAL_LABELS: Record<IntervalKey, string> = {
  '15m': '15 min',
  '1h': '1 hour',
  '6h': '6 hours',
  '1d': '1 day',
};

const INTERVAL_MS: Record<IntervalKey, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '1d': 24 * 60 * 60_000,
};

/** Sensible default granularity for each range. */
const DEFAULT_INTERVAL: Record<RangeKey, IntervalKey> = {
  '24h': '1h',
  '7d': '6h',
  '30d': '1d',
};

const COLOR_COMPLETED = '#70B77E';
const COLOR_FAILED = '#F0544F';

function rangeToDateRange(key: RangeKey): DateRange {
  const to = new Date();
  const from = new Date(to);
  switch (key) {
    case '24h':
      from.setHours(from.getHours() - 24);
      break;
    case '7d':
      from.setDate(from.getDate() - 7);
      break;
    case '30d':
      from.setDate(from.getDate() - 30);
      break;
  }
  return { from, to };
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/** p-th percentile (0..1) of an ascending-sorted numeric array. */
function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const idx = Math.ceil(p * sortedAsc.length) - 1;
  const clamped = Math.min(Math.max(idx, 0), sortedAsc.length - 1);
  return sortedAsc[clamped];
}

interface CountBucket {
  t: number;
  label: string;
  completed: number;
  failed: number;
  total: number;
}

export function RequestAnalytics({ fetchJobs, statsHref }: RequestAnalyticsProps) {
  const [rangeKey, setRangeKey] = useState<RangeKey>('24h');
  const [interval, setInterval] = useState<IntervalKey>('1h');
  const [jobs, setJobs] = useState<AnalyticsJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refetch whenever the range changes; interval changes only recompute
  // client-side, so they don't trigger a network round-trip.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchJobs(rangeToDateRange(rangeKey))
      .then((result) => {
        if (!cancelled) setJobs(result);
      })
      .catch((ex) => {
        if (!cancelled) {
          setError(ex instanceof Error ? ex.message : 'Failed to load analytics');
          setJobs([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchJobs, rangeKey]);

  const handleRangeChange = useCallback((key: RangeKey) => {
    setRangeKey(key);
    setInterval(DEFAULT_INTERVAL[key]);
  }, []);

  const buckets = useMemo<CountBucket[]>(() => {
    const step = INTERVAL_MS[interval];
    const range = rangeToDateRange(rangeKey);
    if (!range.from || !range.to) return [];

    const start = Math.floor(range.from.getTime() / step) * step;
    const end = range.to.getTime();
    const labelFmt =
      step >= INTERVAL_MS['1d']
        ? 'MMM d'
        : rangeKey === '24h'
          ? 'HH:mm'
          : 'M/d HH:mm';

    const map = new Map<number, { completed: number; failed: number }>();
    for (let t = start; t <= end; t += step) {
      map.set(t, { completed: 0, failed: 0 });
    }

    for (const job of jobs) {
      const ts = new Date(job.created_at).getTime();
      if (Number.isNaN(ts)) continue;
      const bucket = Math.floor(ts / step) * step;
      const entry = map.get(bucket);
      if (!entry) continue;
      if (job.status === 'failed') entry.failed += 1;
      else entry.completed += 1;
    }

    return Array.from(map.entries()).map(([t, v]) => ({
      t,
      label: format(new Date(t), labelFmt),
      completed: v.completed,
      failed: v.failed,
      total: v.completed + v.failed,
    }));
  }, [jobs, rangeKey, interval]);

  const totalRequests = useMemo(
    () => buckets.reduce((sum, b) => sum + b.total, 0),
    [buckets],
  );

  const recentErrors = useMemo(
    () =>
      jobs
        .filter((j) => j.status === 'failed')
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .slice(0, 20),
    [jobs],
  );

  const { p90, slowest } = useMemo(() => {
    const completed = jobs.filter(
      (j) => j.status === 'completed' && j.duration_seconds != null,
    );
    const durations = completed
      .map((j) => j.duration_seconds as number)
      .sort((a, b) => a - b);
    const slowestJobs = [...completed]
      .sort(
        (a, b) =>
          (b.duration_seconds as number) - (a.duration_seconds as number),
      )
      .slice(0, 10);
    return { p90: percentile(durations, 0.9), slowest: slowestJobs };
  }, [jobs]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
      {/* Header + controls */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Request analytics</h2>
          <p className="text-sm text-slate-500">
            Request counts, recent errors and slowest requests
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Range selector */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
              <button
                key={key}
                onClick={() => handleRangeChange(key)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  rangeKey === key
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {RANGE_LABELS[key]}
              </button>
            ))}
          </div>
          {/* Interval selector */}
          <select
            value={interval}
            onChange={(e) => setInterval(e.target.value as IntervalKey)}
            className="px-2 py-1 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            title="Bucket interval"
          >
            {(Object.keys(INTERVAL_LABELS) as IntervalKey[]).map((key) => (
              <option key={key} value={key}>
                {INTERVAL_LABELS[key]}
              </option>
            ))}
          </select>
          {statsHref && (
            <Link
              to={statsHref}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors"
            >
              Full stats
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
          {error}
        </div>
      )}

      {/* Request counts — full width */}
      <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Request counts</h3>
          <div className="text-right">
            <span className="text-lg font-bold text-slate-700">{totalRequests}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
          </div>
        </div>
        <div className="h-64">
          {loading && jobs.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Loading analytics…
            </div>
          ) : totalRequests > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px',
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={24}
                  formatter={(value: string) => (
                    <span className="text-slate-600 text-xs">{value}</span>
                  )}
                />
                <Bar dataKey="completed" name="Completed" stackId="a" fill={COLOR_COMPLETED} />
                <Bar dataKey="failed" name="Failed" stackId="a" fill={COLOR_FAILED} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              No requests in this period
            </div>
          )}
        </div>
      </div>

      {/* Recent errors + Slowest requests — 50/50 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Recent errors */}
        <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Recent errors</h3>
            <div className="text-right">
              <span className="text-lg font-bold text-[#F0544F]">{recentErrors.length}</span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">shown</span>
            </div>
          </div>
          {recentErrors.length > 0 ? (
            <ul className="space-y-2 overflow-y-auto max-h-64 pr-1">
              {recentErrors.map((job) => (
                <li
                  key={job.id}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] text-slate-500 truncate">
                      {job.id.slice(0, 8)}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-700 line-clamp-2 break-words">
                    {job.error || 'Unknown error'}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-[#70B77E]">
              No errors in this period
            </div>
          )}
        </div>

        {/* Slowest requests (p90) */}
        <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Slowest requests</h3>
            <div className="text-right">
              <span className="text-lg font-bold text-slate-700">
                {p90 > 0 ? formatDuration(p90) : '—'}
              </span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">p90</span>
            </div>
          </div>
          {slowest.length > 0 ? (
            <ul className="space-y-2 overflow-y-auto max-h-64 pr-1">
              {slowest.map((job) => (
                <li
                  key={job.id}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 flex items-center justify-between gap-2"
                >
                  <span className="font-mono text-[11px] text-slate-500 truncate">
                    {job.id.slice(0, 8)}
                  </span>
                  <span className="text-xs font-medium text-slate-700 shrink-0">
                    {formatDuration(job.duration_seconds as number)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
              No completed requests in this period
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
